import type { IconData } from "@loot-os/icons/types";
import { type CSSProperties, type ReactNode, type Ref, useId, useMemo } from "react";

import { tv } from "../foundations/cn.js";
import { useMediaQuery } from "../foundations/useMediaQuery.js";
import { IconButton } from "./IconButton.js";
import {
  SIDE_DOCK_DOCKS,
  SIDE_DOCK_FLOATS,
  SIDE_DOCK_TAB_MIDDLE,
  SideDockContext,
  type SideDockContextValue,
  type SideDockSide,
  useInstantWhileChanging,
  useSideDock,
} from "./useSideDock.js";

/*
 * Classes are complete literals (Tailwind reads this file as text). By the screen's width:
 * - Below md: the panel fills the space between the phone's top bar and tab bar
 *   (`--top-bar-inset`, `--bottom-bar-inset`, which PageShell sets), under both, with no scrim.
 *   Closed, it rests under the screen's foot, hidden.
 * - From md: 320px at the page's edge, from under the top nav to the screen's foot, its tab on its
 *   outer edge. Closed, it slides its own width out and the tab rests at the screen's edge.
 * - md to 1440px: it floats over the page, with the float shadow while open.
 * - From 1441px (90.0625rem): docked. The root keeps its room (`--dock-room`, open or not), and
 *   while it is open every content width (`max-w-store`) slides half its width away from it
 *   (`--dock-shift`), so nothing is laid out again.
 * Only translate, visibility and opacity move, on the theme's slow duration; none under reduced
 * motion.
 */
const sideDockStyles = tv({
  slots: {
    root: "[--side-dock-width:20rem] min-[90.0625rem]:[--dock-room:var(--side-dock-width)]",
    panel: [
      "fixed z-30 flex flex-col",
      "inset-x-0 top-(--top-bar-inset,0px) bottom-(--bottom-bar-inset,0px)",
      "not-data-open:invisible not-data-open:translate-y-full",
      "md:inset-x-auto md:bottom-0 md:w-(--side-dock-width)",
      "md:not-data-open:visible md:not-data-open:translate-y-0",
      // The float shadow is a layer of its own whose opacity fades with the slide.
      "before:pointer-events-none before:absolute before:inset-0 before:shadow-float before:opacity-0",
      "md:max-[90.0625rem]:data-open:before:opacity-100",
      "before:transition-opacity before:duration-slow before:ease-standard",
      "transition-[translate,visibility] duration-slow ease-standard",
      "motion-reduce:transition-none data-instant:transition-none",
      "motion-reduce:before:transition-none data-instant:before:transition-none",
    ],
    content: "flex h-full min-h-0 w-full min-w-0 flex-col bg-bg-surface md:border-border-subtle",
    tab: [
      "absolute shadow-float max-md:hidden",
      "bottom-[calc(var(--side-dock-tab-middle,6.5rem)-var(--spacing-control-sm)/2)]",
    ],
  },
  variants: {
    side: {
      left: {
        root: "min-[90.0625rem]:data-open:[--dock-shift:calc(var(--side-dock-width)/2)]",
        panel: "md:left-0 md:not-data-open:-translate-x-full",
        content: "md:border-r",
        tab: "left-full rounded-l-none",
      },
      right: {
        root: "min-[90.0625rem]:data-open:[--dock-shift:calc(var(--side-dock-width)/-2)]",
        panel: "md:right-0 md:not-data-open:translate-x-full",
        content: "md:border-l",
        tab: "right-full rounded-r-none",
      },
    },
  },
  defaultVariants: { side: "left" },
});

export interface SideDockRootProps {
  /** Whether the dock is open in the screen's layout: the page's state. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The page edge it sits on. */
  side?: SideDockSide;
  /**
   * The panel's content's id, for a control outside the dock that opens it (a tab bar's Chat, as
   * `aria-controls`). One is made when left out.
   */
  contentId?: string;
  /** The page and the `SideDockPanel`. */
  children: ReactNode;
  className?: string;
}

/**
 * The page's frame for a side dock: it holds the page and the `SideDockPanel`, keeps the docked
 * panel's room clear from 1441px (`--dock-room`) and, while it is open, moves every `max-w-store`
 * width beside it (`--dock-shift`). The open state is the page's.
 */
export function SideDockRoot({
  open,
  onOpenChange,
  side = "left",
  contentId: givenId,
  children,
  className,
}: SideDockRootProps) {
  const madeId = useId();
  const contentId = givenId ?? madeId;
  const context = useMemo<SideDockContextValue>(
    () => ({ open, onOpenChange, side, contentId }),
    [open, onOpenChange, side, contentId],
  );
  return (
    <div
      data-slot="side-dock"
      data-side={side}
      data-open={open ? "" : undefined}
      className={sideDockStyles({ side }).root({ className })}
    >
      <SideDockContext value={context}>{children}</SideDockContext>
    </div>
  );
}

export interface SideDockPanelProps {
  /**
   * The landmark's name. Leave it out when what it holds is already a named landmark (a chat panel
   * is a region named "Chat"), so a reader meets one name, not two nested.
   */
  label?: string;
  /** The tab's icon, the same open or closed. */
  tabIcon: IconData;
  /** The tab's name while the panel is hidden: "Show chat". */
  showLabel: string;
  /** The tab's name while the panel is open: "Hide chat". */
  hideLabel: string;
  /**
   * The tab's middle, in px above the panel's foot (6.5rem by default). A panel whose row moves
   * places it with `placeSideDockTab(panel, middle)` through `ref` instead.
   */
  tabMiddle?: number;
  ref?: Ref<HTMLElement>;
  children: ReactNode;
  className?: string;
}

/**
 * The side panel, inside a `SideDockRoot`: docked beside the page from 1441px, floating over the
 * page's edge from md, and filling the space between the phone's bars below. Hidden, it stays
 * mounted and out of reach (`inert`), so what it holds keeps its state. From md the tab on its
 * outer edge hides and shows it; on a phone the page opens it from the tab bar.
 */
export function SideDockPanel({
  label,
  tabIcon,
  showLabel,
  hideLabel,
  tabMiddle,
  ref,
  children,
  className,
}: SideDockPanelProps) {
  const { open, onOpenChange, side, contentId } = useSideDock();
  // Crossing md trades the phone's resting place for the side's, and crossing the dock point
  // trades one open state for the other: both change at once, never slide across the screen.
  const floatsChanging = useInstantWhileChanging(useMediaQuery(SIDE_DOCK_FLOATS));
  const docksChanging = useInstantWhileChanging(useMediaQuery(SIDE_DOCK_DOCKS));
  const instant = floatsChanging || docksChanging;
  const styles = sideDockStyles({ side });
  const style =
    tabMiddle === undefined
      ? undefined
      : ({ [SIDE_DOCK_TAB_MIDDLE]: `${tabMiddle}px` } as CSSProperties);
  return (
    <aside
      ref={ref}
      aria-label={label}
      data-slot="side-dock-panel"
      data-open={open ? "" : undefined}
      data-instant={instant ? "" : undefined}
      style={style}
      className={styles.panel({ className })}
    >
      <div id={contentId} inert={!open} className={styles.content()}>
        {children}
      </div>
      <IconButton
        icon={tabIcon}
        label={open ? hideLabel : showLabel}
        size="sm"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => onOpenChange(!open)}
        className={styles.tab()}
      />
    </aside>
  );
}
